import JsBarcode from 'jsbarcode'
import brand from '@/branding'

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function formatQuantity(value) {
  const num = Number(value)
  if (Number.isNaN(num)) return ''
  return num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatMoney(value) {
  const num = Number(value)
  if (Number.isNaN(num)) return ''
  return `$${num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function formatDate(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })
}

function barcodeMarkup(code) {
  if (!code) return ''
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  document.body.appendChild(svg)
  try {
    JsBarcode(svg, code, {
      format: 'CODE128',
      width: 2,
      height: 72,
      displayValue: true,
      fontSize: 16,
      margin: 0,
    })
    return svg.outerHTML
  } finally {
    svg.remove()
  }
}

function logoSrc() {
  const src = brand.topbarLogo || ''
  if (!src || /^(data:|https?:|blob:)/.test(src)) return src
  try {
    return new URL(src, window.location.href).href
  } catch (error) {
    return src
  }
}

function printDocument(title, body) {
  const printWindow = window.open('', '_blank', 'width=800,height=900')
  if (!printWindow) return
  const printScript = '<scr' + 'ipt>'
    + 'window.onload = function () {'
    + '  var images = Array.prototype.slice.call(document.images);'
    + '  var pending = images.filter(function (img) { return !img.complete; });'
    + '  if (!pending.length) { window.print(); return; }'
    + '  var left = pending.length;'
    + '  function done() { if (--left <= 0) window.print(); }'
    + '  pending.forEach(function (img) { img.onload = done; img.onerror = done; });'
    + '};'
    + '</scr' + 'ipt>'
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(title)}</title>
        <style>
          body { font-family: Arial, sans-serif; color: #111; margin: 24px; }
          .sheet-head { display: flex; align-items: center; gap: 16px; margin-bottom: 8px; }
          .logo { height: 64px; width: auto; max-width: 220px; object-fit: contain; }
          h1 { font-size: 20px; margin: 0 0 4px; letter-spacing: 0.04em; }
          .subtitle { margin: 0; color: #444; font-size: 13px; }
          .barcode { margin: 8px 0 16px; }
          .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 24px; font-size: 13px; margin-bottom: 16px; }
          .meta strong { display: inline-block; min-width: 96px; }
          table { width: 100%; border-collapse: collapse; font-size: 13px; }
          th, td { border: 1px solid #222; padding: 6px 8px; text-align: left; }
          th { background: #f3f3f3; }
          td.num, th.num { text-align: right; }
          .comments { margin-top: 14px; font-size: 13px; }
          .total { margin-top: 10px; text-align: right; font-size: 14px; }
          .signs { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 28px; margin-top: 56px; }
          .sign { border-top: 1px solid #222; padding-top: 6px; text-align: center; font-size: 12px; }
        </style>
      </head>
      <body>
        ${body}
        ${printScript}
      </body>
    </html>
  `)
  printWindow.document.close()
}

function voucherDocument(voucher, options) {
  const rows = (voucher.items || []).map((item) => `
    <tr>
      <td>${escapeHtml(item.part_barcode)}</td>
      <td>${escapeHtml(item.part_name)}</td>
      <td class="num">${escapeHtml(formatQuantity(item.quantity))} ${escapeHtml(item.part_unit_of_measure || '')}</td>
      <td class="num">${escapeHtml(formatMoney(item.unit_cost_snapshot))}</td>
      <td class="num">${escapeHtml(formatMoney(item.subtotal))}</td>
    </tr>
  `).join('')

  const meta = options.meta.map(([label, value]) => (
    `<div><strong>${escapeHtml(label)}:</strong> ${escapeHtml(value || '—')}</div>`
  )).join('')

  const signs = options.signs.map((label) => `<div class="sign">${escapeHtml(label)}</div>`).join('')
  const logo = logoSrc()

  return `
    <div class="sheet-head">
      ${logo ? `<img class="logo" src="${escapeHtml(logo)}" alt="${escapeHtml(brand.displayName || 'Logo')}" />` : ''}
      <div>
        <h1>${escapeHtml(options.heading)}</h1>
        <p class="subtitle">${escapeHtml(options.subtitle)}</p>
      </div>
    </div>
    <div class="barcode">${barcodeMarkup(voucher.voucher_number)}</div>
    <div class="meta">${meta}</div>
    <table>
      <thead>
        <tr>
          <th>Código</th>
          <th>Pieza</th>
          <th class="num">Cantidad</th>
          <th class="num">Costo unitario</th>
          <th class="num">Subtotal</th>
        </tr>
      </thead>
      <tbody>
        ${rows || '<tr><td colspan="5">Sin piezas</td></tr>'}
      </tbody>
    </table>
    <p class="total"><strong>Total:</strong> ${escapeHtml(formatMoney(voucher.total))}</p>
    ${voucher.comments ? `<p class="comments"><strong>Comentarios:</strong> ${escapeHtml(voucher.comments)}</p>` : ''}
    <div class="signs">${signs}</div>
  `
}

export function printEntryVoucher(voucher) {
  if (!voucher) return
  printDocument(
    `Vale de entrada ${voucher.voucher_number || ''}`,
    voucherDocument(voucher, {
      heading: 'VALE DE ENTRADA',
      subtitle: 'Ingreso de piezas a almacén',
      meta: [
        ['Folio', voucher.voucher_number],
        ['Fecha', formatDate(voucher.created_at)],
        ['Autorizó', voucher.authorized_by_name],
        ['Compró', voucher.purchased_by_name],
        ['Proveedor', voucher.supplier],
        ...(voucher.purchase_order_number ? [['Orden de compra', voucher.purchase_order_number]] : []),
      ],
      signs: ['Autoriza', 'Compró', 'Recibe almacén'],
    }),
  )
}

const LETTER_MONTHS = ['ene.', 'feb.', 'mar.', 'abr.', 'may.', 'jun.', 'jul.', 'ago.', 'sep.', 'oct.', 'nov.', 'dic.']

function letterDate(value) {
  if (!value) return ''
  const text = String(value)
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
    const [year, month, day] = text.slice(0, 10).split('-')
    return `${Number(day)}/${LETTER_MONTHS[Number(month) - 1]}/${year}`
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return `${date.getDate()}/${LETTER_MONTHS[date.getMonth()]}/${date.getFullYear()}`
}

function belowThousand(n) {
  const ones = ['', 'un', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve']
  const teens = ['diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve']
  const tens = ['', '', 'veinte', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa']
  const hundreds = ['', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos', 'seiscientos', 'setecientos', 'ochocientos', 'novecientos']
  if (n === 0) return ''
  if (n === 100) return 'cien'
  const h = Math.floor(n / 100)
  const rest = n % 100
  let words = hundreds[h]
  if (rest < 10) words = `${words} ${ones[rest]}`.trim()
  else if (rest < 20) words = `${words} ${teens[rest - 10]}`.trim()
  else if (rest < 30) {
    const special = ['veinte', 'veintiún', 'veintidós', 'veintitrés', 'veinticuatro', 'veinticinco', 'veintiséis', 'veintisiete', 'veintiocho', 'veintinueve']
    words = `${words} ${special[rest - 20]}`.trim()
  } else {
    const t = Math.floor(rest / 10)
    const o = rest % 10
    words = `${words} ${tens[t]}${o ? ` y ${ones[o]}` : ''}`.trim()
  }
  return words
}

function pesosEnLetra(amount) {
  const value = Number(amount) || 0
  const pesos = Math.floor(value + 0.0001)
  const cents = Math.round((value - pesos) * 100)
  const chunks = [
    [1000000, 'millón', 'millones'],
    [1000, 'mil', 'mil'],
  ]
  let rest = pesos
  const parts = []
  chunks.forEach(([size, one, many]) => {
    const count = Math.floor(rest / size)
    rest %= size
    if (!count) return
    if (size === 1000 && count === 1) parts.push('mil')
    else parts.push(`${belowThousand(count)} ${count === 1 ? one : many}`.trim())
  })
  if (rest || !parts.length) parts.push(pesos === 0 ? 'cero' : belowThousand(rest))
  const words = parts.join(' ').replace(/\s+/g, ' ').trim()
  const titled = words.charAt(0).toUpperCase() + words.slice(1)
  const noun = pesos === 1 ? 'peso' : 'pesos'
  return `(${titled} ${noun} ${String(cents).padStart(2, '0')}/100 m.n.)`
}

function companyBlock() {
  const lines = [brand.legalName, brand.addressLine1, brand.addressLine2].filter(Boolean)
  const rfc = brand.rfc ? `RFC: ${brand.rfc}` : ''
  return `${lines.map((line) => `<div>${escapeHtml(line)}</div>`).join('')}${rfc ? `<div>${escapeHtml(rfc)}</div>` : ''}`
}

function blankRows(count, cols) {
  return Array.from({ length: count }, () => `<tr class="blank">${'<td></td>'.repeat(cols)}</tr>`).join('')
}

const LETTER_CSS = `
  @page { size: letter; margin: 12mm 14mm; }
  * { box-sizing: border-box; }
  body { margin: 0; color: #111; font-family: Arial, Helvetica, sans-serif; font-size: 11px; }
  .head { display: grid; grid-template-columns: 150px 1fr auto; gap: 12px; align-items: start; }
  .logo { width: 140px; height: auto; }
  h1 { margin: 0 0 6px; font-size: 22px; font-weight: 700; }
  .company { font-size: 11px; line-height: 1.35; font-weight: 700; }
  .folio { display: grid; grid-template-columns: auto auto; gap: 4px 10px; text-align: right; font-size: 11px; }
  .folio strong { display: block; }
  .parties { display: grid; grid-template-columns: 1.1fr 1fr; gap: 12px; margin-top: 14px; }
  .label { font-size: 10px; font-weight: 700; letter-spacing: 0.03em; }
  .party-name { font-weight: 700; margin: 2px 0; }
  .ship { min-height: 58px; border-bottom: 1px solid #222; }
  .facts { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-top: 10px; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; }
  th { text-align: left; font-size: 10px; border-bottom: 1px solid #222; padding: 4px 6px; }
  td { padding: 3px 6px; vertical-align: top; font-size: 11px; }
  td.num, th.num { text-align: right; }
  tr.blank td { height: 18px; }
  .foot { display: grid; grid-template-columns: 1.4fr 0.8fr; gap: 24px; margin-top: 18px; align-items: end; }
  .notes { min-height: 70px; }
  .words { margin-top: 16px; font-size: 11px; }
  .signs { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; margin-top: 28px; }
  .sign { border-top: 1px solid #222; padding-top: 4px; font-size: 11px; font-weight: 700; }
  .totals { width: 180px; margin-left: auto; }
  .totals div { display: flex; justify-content: space-between; gap: 12px; margin: 3px 0; }
  .totals .grand { font-weight: 700; margin-top: 6px; }
`

function openLetter(title, body) {
  const printWindow = window.open('', '_blank', 'width=900,height=1100')
  if (!printWindow) return
  const printScript = '<scr' + 'ipt>window.onload = function () { window.print(); }</scr' + 'ipt>'
  printWindow.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8" /><title>${escapeHtml(title)}</title><style>${LETTER_CSS}</style></head><body>${body}${printScript}</body></html>`)
  printWindow.document.close()
}

export function printPurchaseOrder(order) {
  if (!order) return
  const lines = order.items || []
  let subtotal = 0
  const rows = lines.map((item) => {
    const qty = Number(item.quantity) || 0
    const price = Number(item.unit_cost) || 0
    const discount = Number(item.discount_percent) || 0
    const amount = qty * price * (1 - discount / 100)
    subtotal += amount
    return `<tr>
      <td>${escapeHtml(item.part_barcode)}</td>
      <td>${escapeHtml(item.part_name)}</td>
      <td>${escapeHtml(item.part_unit_of_measure || 'PZA')}</td>
      <td class="num">${escapeHtml(formatQuantity(item.quantity))}</td>
      <td class="num">${escapeHtml(formatMoney(item.unit_cost).replace('$', ''))}</td>
      <td class="num">${discount ? escapeHtml(discount.toFixed(2)) : ''}</td>
      <td class="num">${escapeHtml(amount.toFixed(2))}</td>
    </tr>`
  }).join('')
  const iva = subtotal * 0.16
  const total = subtotal + iva
  const ship = order.ship_to || [brand.addressLine1, brand.addressLine2].filter(Boolean).join(', ')
  const body = `
    <div class="head">
      <img class="logo" src="${escapeHtml(logoSrc())}" alt="" />
      <div>
        <h1>Orden de compra</h1>
        <div class="company">${companyBlock()}</div>
      </div>
      <div class="folio">
        <div><strong>FECHA</strong>${escapeHtml(letterDate(order.created_at))}</div>
        <div><strong>FOLIO</strong>${escapeHtml(order.order_number)}</div>
      </div>
    </div>
    <div class="parties">
      <div>
        <div class="label">PROVEEDOR ${escapeHtml(order.provider_code || '')}</div>
        <div class="party-name">${escapeHtml(order.provider_name || '')}</div>
        <div>${escapeHtml(order.provider_address || '')}</div>
        <div>TELEFONOS: ${escapeHtml(order.provider_phone || '')}</div>
        <div>FAX: ${escapeHtml(order.provider_fax || '')}</div>
      </div>
      <div>
        <div class="label">Consignatario / SHIP TO:</div>
        <div class="ship">${escapeHtml(ship)}</div>
      </div>
    </div>
    <div class="facts">
      <div><div class="label">ENTREGA</div>${escapeHtml(letterDate(order.expected_on))}</div>
      <div><div class="label">CONDICIONES</div>${escapeHtml(order.payment_terms || '')}</div>
      <div><div class="label">SOLICITA:</div>${escapeHtml(order.requested_by_name || '')}</div>
      <div><div class="label">CONTACTO:</div>${escapeHtml(order.provider_contact || '')}</div>
    </div>
    <table>
      <thead>
        <tr>
          <th>Artículo</th>
          <th>Nombre</th>
          <th>U. med.</th>
          <th class="num">Unidades</th>
          <th class="num">Precio</th>
          <th class="num">Descto.</th>
          <th class="num">Importe</th>
        </tr>
      </thead>
      <tbody>
        ${rows}
        ${blankRows(Math.max(8, 14 - lines.length), 7)}
      </tbody>
    </table>
    <div class="foot">
      <div>
        <div class="label">OBSERVACIONES:</div>
        <div class="notes">${escapeHtml(order.comments || '')}</div>
        <div class="words">${escapeHtml(pesosEnLetra(total))}</div>
        <div class="signs">
          <div class="sign">SOLICITA:</div>
          <div class="sign">AUTORIZA:</div>
        </div>
      </div>
      <div class="totals">
        <div><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
        <div><span>Iva 16%</span><span>${iva.toFixed(2)}</span></div>
        <div class="grand"><span>Total</span><span>${total.toFixed(2)}</span></div>
      </div>
    </div>
  `
  openLetter(`Orden de compra ${order.order_number || ''}`, body)
}

export function printToolLoan(loan) {
  if (!loan) return
  const lines = loan.items || []
  const rows = lines.map((item) => `<tr>
    <td>${escapeHtml(item.part_barcode)}</td>
    <td>${escapeHtml(item.part_name)}</td>
    <td>${escapeHtml(item.part_unit_of_measure || 'PZA')}</td>
    <td class="num">${escapeHtml(formatQuantity(item.quantity))}</td>
  </tr>`).join('')
  const body = `
    <div class="head">
      <img class="logo" src="${escapeHtml(logoSrc())}" alt="" />
      <div>
        <h1>Vale de préstamo</h1>
        <div class="company">${companyBlock()}</div>
      </div>
      <div class="folio">
        <div><strong>FECHA</strong>${escapeHtml(letterDate(loan.delivered_at))}</div>
        <div><strong>FOLIO</strong>${escapeHtml(loan.voucher_number)}</div>
      </div>
    </div>
    <div class="parties">
      <div>
        <div class="label">RECIBE</div>
        <div class="party-name">${escapeHtml(loan.received_by_name || '')}</div>
        <div>ENTREGÓ: ${escapeHtml(loan.delivered_by_name || '')}</div>
        <div>ORDEN: ${escapeHtml(loan.maintenance_order_number || '')}</div>
        <div>UNIDAD: ${escapeHtml(loan.unit_short_id || '')}</div>
      </div>
      <div>
        <div class="label">DEVOLUCIÓN</div>
        <div class="ship">
          Esperada: ${escapeHtml(letterDate(loan.expected_return_at))}<br />
          Devuelta: ${escapeHtml(letterDate(loan.returned_at))}<br />
          Recibió: ${escapeHtml(loan.returned_to_name || '')}<br />
          Estatus: ${escapeHtml(loan.status_label || '')}
        </div>
      </div>
    </div>
    <table>
      <thead>
        <tr>
          <th>Artículo</th>
          <th>Nombre</th>
          <th>U. med.</th>
          <th class="num">Unidades</th>
        </tr>
      </thead>
      <tbody>
        ${rows}
        ${blankRows(Math.max(8, 14 - lines.length), 4)}
      </tbody>
    </table>
    <div class="foot">
      <div>
        <div class="label">OBSERVACIONES:</div>
        <div class="notes">${escapeHtml(loan.comments || '')}</div>
        <div class="signs">
          <div class="sign">ENTREGA ALMACÉN</div>
          <div class="sign">RECIBE MECÁNICO</div>
        </div>
      </div>
      <div></div>
    </div>
  `
  openLetter(`Vale de préstamo ${loan.voucher_number || ''}`, body)
}

export function printExitVoucher(voucher) {
  if (!voucher) return
  printDocument(
    `Vale de salida ${voucher.voucher_number || ''}`,
    voucherDocument(voucher, {
      heading: 'VALE DE SALIDA',
      subtitle: 'Salida de piezas de almacén',
      meta: [
        ['Folio', voucher.voucher_number],
        ['Fecha', formatDate(voucher.created_at)],
        ['Autorizó', voucher.authorized_by_name],
        ['Recibió', voucher.received_by_name],
        ['Orden', voucher.maintenance_order_number || 'Sin vincular'],
        ['Requisición', voucher.requisition_number || 'Sin requisición'],
      ],
      signs: ['Autoriza', 'Recibe', 'Entrega almacén'],
    }),
  )
}
